<template>
    <div class="page-container">
        <div class="flex items-center justify-between mb-8">
            <div class="flex items-center gap-4">
                <div class="bg-red-600 p-3 rounded-2xl shadow-lg">
                    <LucideIcon :size="24" class="text-white">
                        <path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/>
                    </LucideIcon>
                </div>
                <div>
                    <h2 class="page-title text-2xl font-bold text-slate-900">{{ $t('household.depreciation.title') }}</h2>
                    <p class="section-subtitle text-slate-500">{{ $t('household.depreciation.subtitle') }}</p>
                </div>
            </div>
            <button
                @click="openAddDialog"
                class="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md active:scale-95"
            >
                <LucideIcon :size="18"><path d="M5 12h14"/><path d="M12 5v14"/></LucideIcon>
                {{ $t('household.depreciation.setupBtn') }}
            </button>
        </div>

        <!-- Итоги амортизации -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.depreciation.statInitial') }}</p>
                        <p class="text-3xl font-extrabold text-slate-900">{{ formatPrice(totalInitialCost) }}</p>
                    </div>
                    <div class="bg-blue-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-blue-600"><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></LucideIcon>
                    </div>
                </div>
            </div>
            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.depreciation.statAccumulated') }}</p>
                        <p class="text-3xl font-extrabold text-red-600">{{ formatPrice(totalDepreciation) }}</p>
                    </div>
                    <div class="bg-red-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-red-600"><path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/></LucideIcon>
                    </div>
                </div>
            </div>
            <div class="stat-card">
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-sm font-bold text-slate-400 mb-2 uppercase tracking-tight">{{ $t('household.depreciation.statResidual') }}</p>
                        <p class="text-3xl font-extrabold text-green-600">{{ formatPrice(totalResidualValue) }}</p>
                    </div>
                    <div class="bg-green-50 p-3 rounded-xl">
                        <LucideIcon :size="24" class="text-green-600"><path d="m22 7-8.5 8.5-5-5L2 17"/><path d="M16 7h6v6"/></LucideIcon>
                    </div>
                </div>
            </div>
        </div>

        <!-- Список -->
        <div v-if="loading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <div v-else class="space-y-4">
            <div v-for="dep in depreciations" :key="dep.id" class="statement-card p-6 hover:shadow-lg transition-all border-l-4" :style="{ borderLeftColor: getProgressColor(calculateDepreciationPercent(dep)) }">
                <div class="flex items-start justify-between">
                    <div class="flex-1">
                        <div class="flex items-center gap-3 mb-3">
                            <h3 class="font-black text-slate-900 text-lg leading-tight">{{ getNomenclatureName(dep.nomenclatureId) }}</h3>
                            <span :class="['px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border', dep.type === 'monthly' ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-purple-50 text-purple-600 border-purple-100']">
                                {{ dep.type === 'monthly' ? $t('household.depreciation.monthly') : $t('household.depreciation.yearly') }}
                            </span>
                            <span class="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest">
                                {{ dep.rate }}{{ $t('household.depreciation.ratePercentSuffix') }}
                            </span>
                        </div>
                        
                        <div class="grid grid-cols-2 lg:grid-cols-4 gap-6">
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.depreciation.labelInitial') }}</p>
                                <p class="font-bold text-slate-900">{{ formatPrice(getNomenclaturePrice(dep.nomenclatureId)) }}</p>
                            </div>
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.depreciation.labelWear') }}</p>
                                <p class="font-bold text-red-600">{{ formatPrice(getNomenclaturePrice(dep.nomenclatureId) - calculateCurrentValue(dep)) }}</p>
                            </div>
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.depreciation.labelResidual') }}</p>
                                <p class="font-black text-green-600">{{ formatPrice(calculateCurrentValue(dep)) }}</p>
                            </div>
                            <div>
                                <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{{ $t('household.depreciation.labelStart') }}</p>
                                <p class="font-bold text-slate-900">{{ formatDate(dep.startDate) }}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="flex flex-col items-end gap-4">
                        <div class="flex gap-2">
                            <button
                                @click="editItem(dep)"
                                class="w-8 h-8 rounded-lg flex items-center justify-center bg-slate-50 text-slate-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                            >
                                <LucideIcon :size="16"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></LucideIcon>
                            </button>
                        </div>
                        <div class="text-right">
                            <span :class="['text-2xl font-black', getTextColor(calculateDepreciationPercent(dep))]">
                                {{ calculateDepreciationPercent(dep).toFixed(0) }}%
                            </span>
                            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest">{{ $t('household.depreciation.wearLabel') }}</p>
                        </div>
                    </div>
                </div>

                <!-- Прогресс бар -->
                <div class="mt-6">
                    <div class="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                        <div
                            class="h-full rounded-full transition-all duration-1000"
                            :class="getBgColor(calculateDepreciationPercent(dep))"
                            :style="{ width: `${Math.min(100, calculateDepreciationPercent(dep))}%` }"
                        />
                    </div>
                </div>
            </div>

            <div v-if="depreciations.length === 0" class="empty-state-card">
                <LucideIcon :size="48" class="empty-icon">
                    <path d="m22 17-8.5-8.5-5 5L2 7"/><path d="M16 17h6v-6"/>
                </LucideIcon>
                <p>{{ $t('household.depreciation.empty') }}</p>
                <button
                    @click="openAddDialog"
                    class="link-btn"
                >
                    {{ $t('household.depreciation.emptySetup') }}
                </button>
            </div>
        </div>

        <!-- Диалог -->
        <div v-if="isDialogOpen" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div class="bg-white rounded-3xl p-8 w-full max-w-md shadow-2xl scale-in">
                <div class="flex items-center gap-4 mb-6">
                    <div class="bg-blue-600 p-2.5 rounded-xl shadow-lg shadow-blue-200">
                        <LucideIcon :size="20" class="text-white"><path d="M12 2v20"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></LucideIcon>
                    </div>
                    <h3 class="text-xl font-black text-slate-900 leading-tight">
                        {{ editingItem ? $t('household.depreciation.dialogEditTitle') : $t('household.depreciation.dialogAddTitle') }}
                    </h3>
                </div>

                <form @submit.prevent="handleSubmit" class="space-y-6">
                    <div>
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.depreciation.nomenclatureLabel') }}</label>
                        <select
                            required
                            v-model="formData.nomenclatureId"
                            class="form-input w-full"
                        >
                            <option value="">{{ $t('household.depreciation.chooseNomenclature') }}</option>
                            <option v-for="n in nomenclatures" :key="n.id" :value="n.id">
                                {{ n.name }} ({{ formatPrice(n.price) }})
                            </option>
                        </select>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.depreciation.periodLabel') }}</label>
                            <select v-model="formData.type" class="form-input w-full">
                                <option value="monthly">{{ $t('household.depreciation.monthly') }}</option>
                                <option value="yearly">{{ $t('household.depreciation.yearly') }}</option>
                            </select>
                        </div>
                        <div>
                            <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.depreciation.rateLabel') }}</label>
                            <input
                                type="number"
                                required
                                min="0"
                                max="100"
                                step="0.1"
                                v-model.number="formData.rate"
                                class="form-input w-full"
                                placeholder="0.0"
                            />
                        </div>
                    </div>

                    <div>
                        <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">{{ $t('household.depreciation.startDateLabel') }}</label>
                        <DatePicker
                            v-model="formData.startDate"
                            format="iso"
                            class="form-input w-full"
                        />
                    </div>

                    <div class="flex gap-4 pt-4">
                        <button type="submit" class="btn-primary-modal flex-1 active:scale-95">
                            {{ editingItem ? $t('common.save') : $t('household.depreciation.activateBtn') }}
                        </button>
                        <button type="button" @click="closeDialog" class="btn-secondary flex-1 active:scale-95">
                            {{ $t('common.cancel') }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LucideIcon from '@/components/common/LucideIcon.vue';
import { getDepreciation, createDepreciation, updateDepreciation } from '@/api/household_special';
import { getNomenclatures } from '@/api/household_nomenclatures';
import DatePicker from '@/components/widgets/DatePicker.vue';

const { t } = useI18n();

const loading = ref(true);
const depreciations = ref([]);
const nomenclatures = ref([]);
const isDialogOpen = ref(false);
const editingItem = ref(null);

const totalInitialCost = computed(() => {
    return depreciations.value.reduce((sum, dep) => sum + getNomenclaturePrice(dep.nomenclatureId), 0);
});

const totalCurrentValue = computed(() => {
    return depreciations.value.reduce((sum, dep) => sum + calculateCurrentValue(dep), 0);
});

const totalDepreciation = computed(() => totalInitialCost.value - totalCurrentValue.value);
const totalResidualValue = computed(() => totalCurrentValue.value);

const formData = ref({
    nomenclatureId: '',
    type: 'monthly',
    rate: 0,
    startDate: new Date().toISOString().split('T')[0],
});

const getProgressColor = (percent) => {
    if (percent < 30) return '#10b981';
    if (percent < 70) return '#f59e0b';
    return '#ef4444';
};

const getTextColor = (percent) => {
    if (percent < 30) return 'text-emerald-600';
    if (percent < 70) return 'text-amber-600';
    return 'text-rose-600';
};

const getBgColor = (percent) => {
    if (percent < 30) return 'bg-emerald-500';
    if (percent < 70) return 'bg-amber-500';
    return 'bg-rose-500';
};

const fetchData = async () => {
    loading.value = true;
    try {
        const [depRes, nomRes] = await Promise.all([
            getDepreciation(),
            getNomenclatures()
        ]);
        depreciations.value = (Array.isArray(depRes.data) ? depRes.data : (depRes.data?.results || [])).map(d => ({
            ...d,
            nomenclatureId: d.nomenclature,
            type: d.depreciation_type === 'month' ? 'monthly' : 'yearly',
            startDate: d.start_date
        }));
        nomenclatures.value = Array.isArray(nomRes.data) ? nomRes.data : (nomRes.data?.results || []);
    } catch (error) {
        console.error('Error fetching depreciation data:', error);
    } finally {
        loading.value = false;
    }
};

const openAddDialog = () => {
    editingItem.value = null;
    formData.value = {
        nomenclatureId: '',
        type: 'monthly',
        rate: 0,
        startDate: new Date().toISOString().split('T')[0],
    };
    isDialogOpen.value = true;
};

const editItem = (item) => {
    editingItem.value = item;
    formData.value = {
        nomenclatureId: item.nomenclatureId,
        type: item.type,
        rate: item.rate,
        startDate: item.startDate,
    };
    isDialogOpen.value = true;
};

const closeDialog = () => {
    isDialogOpen.value = false;
    editingItem.value = null;
};

const handleSubmit = async () => {
    try {
        const payload = {
            nomenclature: formData.value.nomenclatureId,
            depreciation_type: formData.value.type === 'monthly' ? 'month' : 'year',
            rate: formData.value.rate,
            start_date: formData.value.startDate
        };

        if (editingItem.value) {
            await updateDepreciation(editingItem.value.id, payload);
        } else {
            await createDepreciation(payload);
        }
        await fetchData();
        closeDialog();
    } catch (error) {
        console.error('Error saving depreciation:', error);
    }
};

const calculateCurrentValue = (dep) => {
    const nomenclature = nomenclatures.value.find(n => n.id === dep.nomenclatureId);
    if (!nomenclature) return 0;

    const startDate = new Date(dep.startDate);
    const now = new Date();
    const monthsPassed = (now.getFullYear() - startDate.getFullYear()) * 12 + 
                        (now.getMonth() - startDate.getMonth());

    let periods = 0;
    if (dep.type === 'monthly') {
        periods = monthsPassed;
    } else {
        periods = Math.floor(monthsPassed / 12);
    }

    const totalDepreciation = (nomenclature.price * dep.rate / 100) * periods;
    const currentValue = Math.max(0, nomenclature.price - totalDepreciation);

    return currentValue;
};

const calculateDepreciationPercent = (dep) => {
    const nomenclature = nomenclatures.value.find(n => n.id === dep.nomenclatureId);
    if (!nomenclature || nomenclature.price === 0) return 0;
    
    const currentValue = calculateCurrentValue(dep);
    return ((nomenclature.price - currentValue) / nomenclature.price) * 100;
};

const getNomenclatureName = (id) => {
    return nomenclatures.value.find(n => n.id === id)?.name || t('household.shared.unknown');
};

const getNomenclaturePrice = (id) => {
    return nomenclatures.value.find(n => n.id === id)?.price || 0;
};

const formatPrice = (price) => {
    return new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'KZT' }).format(price);
};

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('ru-RU');
};

onMounted(fetchData);
</script>

<style scoped>
.page-container { padding: 2rem; background: #f3f3f5; min-height: 100%; }
.page-title { color: #000000; font-size: 1.75rem; font-weight: 700; margin: 0; }
.section-subtitle { color: #64748b; font-size: 0.875rem; margin-top: 0.25rem; }

.stat-card {
    background: white;
    padding: 1.5rem 2rem;
    border-radius: 1.25rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.statement-card {
    background: white;
    border-radius: 1.5rem;
    border: 1px solid #eef0f4;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
}

.form-input {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 1rem;
    padding: 0.75rem 1rem;
    color: #1e293b;
    font-size: 0.875rem;
    font-weight: 600;
    transition: all 0.2s;
}

.form-input:focus {
    border-color: #3b82f6;
    background: white;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
    outline: none;
}

.scale-in { animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }

.empty-state-card { text-align: center; padding: 5rem 2rem; background: white; border: 1px dashed #e2e8f0; border-radius: 1.25rem; }
.empty-icon { color: #cbd5e1; margin-bottom: 1.5rem; }
.empty-state-card p { color: #64748b; font-size: 1.1rem; margin-bottom: 1.5rem; }
.link-btn { background: #eff6ff; border: none; color: #2563eb; padding: 0.5rem 1rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.link-btn:hover { background: #dbeafe; }
</style>
